/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect } from 'react';

import user from 'js/lib/user';

import { Hidden, breakpoints } from '@coursera/cds-core';
import type { CalendarItem } from '@coursera/grpc-types-degreehome/coursera/proto/degreehome/v1beta1/calendar_item';

// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { MobileCalendarLoader, WithGetDegreeHomeCalendarQuery } from 'bundles/degree-home/components/v2/Calendar';
import { CalendarFilter } from 'bundles/degree-home/components/v2/Calendar/CalendarFilter';
import { getCalendarItemsMap } from 'bundles/degree-home/components/v2/Calendar/CalendarUtils';
import { MobileCalendarItemsList } from 'bundles/degree-home/components/v2/Calendar/MobileCalendarItemsList';
import { useDegreeContext } from 'bundles/degree-home/components/v2/DegreeHomeContext';
import { Card } from 'bundles/program-loggedin-shared/components/Card';

import { CalendarDayPicker } from './CalendarDayPicker';

type CardProps = {
  degreeId: string;
};

type ContentProps = {
  loading: boolean;
  data: { GetDegreeHomeCalendar: { calendarItems: CalendarItem[] } };
  error: Error | undefined | null;
  refetch: () => void;
};

export const MobileCalendarCardContent = ({ loading, data, error, refetch }: ContentProps) => {
  const { setRefetchCalendarItems, setCalendarItemsData } = useDegreeContext();

  useEffect(() => {
    if (data?.GetDegreeHomeCalendar?.calendarItems) {
      setCalendarItemsData(getCalendarItemsMap(data.GetDegreeHomeCalendar.calendarItems));
      setRefetchCalendarItems(() => refetch);
    }
  }, [data, refetch, setCalendarItemsData, setRefetchCalendarItems]);

  if (loading) {
    return <MobileCalendarLoader />;
  }
  if (error) {
    return null;
  }

  return (
    <Card
      css={() => css`
        overflow: hidden;
        ${breakpoints.down('xs')} {
          padding: 0;
        }
      `}
      data-e2e="calendar-card-mobile"
    >
      <CalendarDayPicker />
      <CalendarFilter mobile={true} />
      <MobileCalendarItemsList />
    </Card>
  );
};

export const MobileCalendarCard = ({ degreeId }: CardProps) => {
  return (
    <React.Fragment>
      <Hidden mdUp>
        <WithGetDegreeHomeCalendarQuery userId={user.get().id} degreeId={degreeId}>
          {({ loading, data, error, refetch }) => {
            return <MobileCalendarCardContent loading={loading} data={data} error={error} refetch={refetch} />;
          }}
        </WithGetDegreeHomeCalendarQuery>
      </Hidden>
    </React.Fragment>
  );
};

export default MobileCalendarCard;
